import { Injectable } from "@angular/core";

import { type Versioning, VersioningContentType } from "../models";
import { StorageUtil } from "../utils/storage.util";

/**
 * Service for managing versioned content with local storage persistence.
 * Supports saving, retrieving, and cleaning up old content versions.
 */
@Injectable({ providedIn: "root" })
export class VersioningService {
  private readonly VERSIONING_KEY = "versioning";
  private readonly DAYS_LIMIT = 5;

  /**
   * Retrieves the most recent version of content for a given type and ID.
   *
   * @param {VersioningContentType} type - The content type enumeration
   * @param {string} id - Unique identifier of the content
   * @returns {string} The latest content version or empty string if none exists
   */
  getLastVersion(type: VersioningContentType, id: string): string {
    const versioning = this.getVersions(type);
    const contentObj = versioning.find((obj) => obj.id === id);

    if (!contentObj || contentObj.versions.length === 0) {
      return "";
    }

    const lastVersion = contentObj.versions[contentObj.versions.length - 1];
    return lastVersion.content;
  }

  /**
   * Retrieves all saved versions for a given content type.
   *
   * @param {VersioningContentType} type - The content type enumeration
   * @returns {Versioning[]} Array of versioning objects with IDs and version lists
   */
  getVersions(type: VersioningContentType): Versioning[] {
    return StorageUtil.get<Versioning[]>(this.buildTypedKey(type)) ?? [];
  }

  /**
   * Saves a new version of content for a given type and ID.
   *
   * @param {VersioningContentType} type - The content type enumeration
   * @param {string} contentId - Unique identifier of the content
   * @param {string} content - The HTML content to version
   * @returns {boolean} True if a new version was saved, false if content is unchanged
   */
  saveVersion(type: VersioningContentType, contentId: string, content: string): boolean {
    if (this.getLastVersion(type, contentId) !== content) {
      const versioning = this.getVersions(type);
      const newVersion = { content, date: new Date().toISOString() };

      const contentObj = versioning.find((obj) => obj.id === contentId);
      if (contentObj) {
        contentObj.versions.push(newVersion);
      } else {
        versioning.push({ id: contentId, versions: [newVersion] });
      }

      StorageUtil.set(this.buildTypedKey(type), versioning);
      return true;
    }
    return false;
  }

  /**
   * Cleans up versions older than the configured retention period for all content types.
   */
  cleanOld(): void {
    Object.values(VersioningContentType).forEach((type) => {
      this.cleanOldForType(type);
    });
  }

  /**
   * Removes versions older than the retention limit for a specific content type.
   *
   * @param {VersioningContentType} type - The content type enumeration
   * @private
   */
  private cleanOldForType(type: VersioningContentType): void {
    const versioning = this.getVersions(type);
    const limitDate = new Date();
    limitDate.setDate(limitDate.getDate() - this.DAYS_LIMIT);

    versioning.forEach((obj) => {
      obj.versions = obj.versions.filter((v) => new Date(v.date) >= limitDate);
    });

    StorageUtil.set(this.buildTypedKey(type), versioning);
  }

  /**
   * Constructs the local storage key for a specific content type.
   *
   * @param {VersioningContentType} type - The content type enumeration
   * @returns {string} The constructed storage key
   * @private
   */
  private buildTypedKey(type: VersioningContentType): string {
    return `${this.VERSIONING_KEY}_${type}`;
  }
}
